import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getQuotation,
  updateQuotation,
  addQuotationNote,
  createQuotationDocument,
} from "../../services/quotations";
import {
  uploadFileAndGetUrl,
  getCOA,
  listTranslations as listCOATranslations,
} from "../../services/coa";
import toast from "react-hot-toast";
import AddSupportDocumentModal from "../../components/ui/AddSupportDocumentModal";
import LocalizationModal from "../../components/modals/LocalizationModal";

const ORGANIZATION_TYPES = [
  "Academia – Non-commercial",
  "Academia – Commercially funded",
  "Publicly funded healthcare provider",
  "Privately funded healthcare provider",
  "Publisher – Educational use",
  "Individual Clinical Practice – Not for profit",
  "Individual Clinical Practice – For profit",
  "Nutraceutical Company",
  "Contract Research Organisation (CRO)",
  "Commercial Research – Pharma or medical device company",
  "Translator / Translation Company",
];

const EditQuotation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quotationData, setQuotationData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [isAddDocumentModalOpen, setIsAddDocumentModalOpen] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [isTranslationModalOpen, setIsTranslationModalOpen] = useState(false);
  const [coa, setCoa] = useState(null);
  const [coaTranslations, setCoaTranslations] = useState([]);
  const [formData, setFormData] = useState({
    organizationType: "",
    intendedUse: "",
    translationsRequested: [],
    customTranslationsText: "",
    eCOA: false,
    wantOriginalTranslation: false,
    numUsers: "",
    numUses: "",
    pqTrustNumber: "",
    startDate: "",
    endDate: "",
    territory: [],
    customerAction: { key: "", value: "" },
    contact: {
      fullName: "",
      jobTitle: "",
      email: "",
      phone: "",
    },
    org: {
      name: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      region: "",
      postcode: "",
      country: "",
      vatNumber: "",
    },
  });

  useEffect(() => {
    loadQuotation();
  }, [id]);

  const loadQuotation = async () => {
    try {
      setLoading(true);
      const response = await getQuotation(id);
      if (response?.data) {
        const data = response.data;
        setQuotationData(data);

        let translationsRequested = [];
        if (Array.isArray(data.translationsRequested)) {
          translationsRequested = data.translationsRequested.map((t) =>
            typeof t === "string"
              ? t
              : t?.country && t?.language
                ? `${t.country} - ${t.language}`
                : String(t),
          );
        }

        setFormData({
          organizationType: data.organizationType || "",
          intendedUse: data.intendedUse || "",
          translationsRequested,
          customTranslationsText: data.customTranslationsText || "",
          eCOA: !!data.eCOA,
          wantOriginalTranslation: !!data.wantOriginalTranslation,
          numUsers: data.numUsers ?? "",
          numUses: data.numUses ?? "",
          pqTrustNumber: data.pqTrustNumber || "",
          startDate: data.startDate
            ? new Date(data.startDate).toISOString().split("T")[0]
            : "",
          endDate: data.endDate
            ? new Date(data.endDate).toISOString().split("T")[0]
            : "",
          territory: Array.isArray(data.territory)
            ? data.territory
            : data.territory
              ? [data.territory]
              : [],
          customerAction: {
            key: data.customerAction?.key ?? "",
            value: data.customerAction?.value ?? "",
          },
          contact: {
            fullName: data.contact?.fullName || "",
            jobTitle: data.contact?.jobTitle || "",
            email: data.contact?.email || "",
            phone: data.contact?.phone || "",
          },
          org: {
            name: data.org?.name || "",
            address1: data.org?.address1 || "",
            address2: data.org?.address2 || "",
            city: data.org?.city || "",
            state: data.org?.state || "",
            region: data.org?.region || "",
            postcode: data.org?.postcode || "",
            country: data.org?.country || "",
            vatNumber: data.org?.vatNumber || "",
          },
        });
        setNotes(data.notes || []);
        if (data.coaId?._id || data.coaId) {
          const coaId = data.coaId._id || data.coaId;
          await loadCOAData(coaId);
        }
      } else {
        toast.error("Quotation not found");
        navigate("/quotations");
      }
    } catch (error) {
      console.error("Failed to load quotation:", error);
      toast.error("Failed to load quotation details");
      navigate("/quotations");
    } finally {
      setLoading(false);
    }
  };

  const loadCOAData = async (coaId) => {
    try {
      const [coaResponse, translationsResponse] = await Promise.all([
        getCOA(coaId).catch(() => ({ data: null })),
        listCOATranslations(coaId).catch(() => ({ data: [] })),
      ]);
      if (coaResponse?.data) setCoa(coaResponse.data);
      if (translationsResponse?.data)
        setCoaTranslations(translationsResponse.data);
    } catch (error) {
      console.error("Failed to load COA data:", error);
    }
  };

  const handleTranslationModalSave = ({
    selectedLocales,
    customRequest,
    wantOriginalTranslation,
    territory,
  }) => {
    setFormData((prev) => ({
      ...prev,
      translationsRequested: selectedLocales || prev.translationsRequested,
      customTranslationsText:
        customRequest != null ? customRequest : prev.customTranslationsText,
      wantOriginalTranslation:
        wantOriginalTranslation ?? prev.wantOriginalTranslation,
      territory: territory ? [territory] : prev.territory,
    }));
    setIsTranslationModalOpen(false);
  };

  const handleOpenTranslationModal = async () => {
    if (!quotationData?.coaId?._id && !quotationData?.coaId) {
      toast.error("COA information not available");
      return;
    }
    const coaId = quotationData.coaId._id || quotationData.coaId;
    if (coaTranslations.length === 0) await loadCOAData(coaId);
    setIsTranslationModalOpen(true);
  };

  const getSelectedTranslationsDisplay = () => {
    if (
      formData.translationsRequested.length === 0 &&
      !formData.customTranslationsText
    )
      return "No translations selected";
    const translations = formData.translationsRequested
      .map((t) =>
        typeof t === "string"
          ? t
          : t?.country && t?.language
            ? `${t.country} - ${t.language}`
            : String(t),
      )
      .join(", ");
    if (formData.customTranslationsText)
      return translations
        ? `${translations} + Custom request`
        : "Custom request only";
    return translations || "No translations selected";
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const formattedTranslations = (
        Array.isArray(formData.translationsRequested)
          ? formData.translationsRequested
          : []
      ).map((t) =>
        typeof t === "string"
          ? t
          : t?.country && t?.language
            ? `${t.country} - ${t.language}`
            : String(t),
      );
      const updateData = {
        organizationType: formData.organizationType || undefined,
        intendedUse: formData.intendedUse || undefined,
        translationsRequested: formattedTranslations,
        customTranslationsText: formData.customTranslationsText || "",
        eCOA: formData.eCOA,
        territory: Array.isArray(formData.territory) ? formData.territory : [],
        customerAction: formData.customerAction
          ? {
              key: formData.customerAction.key || "",
              value: formData.customerAction.value || "",
            }
          : undefined,
      };
      if (formData.numUsers !== "")
        updateData.numUsers = parseInt(formData.numUsers, 10);
      if (formData.numUses !== "")
        updateData.numUses = parseInt(formData.numUses, 10);
      if (formData.pqTrustNumber)
        updateData.pqTrustNumber = formData.pqTrustNumber;
      if (formData.startDate) updateData.startDate = formData.startDate;
      if (formData.endDate) updateData.endDate = formData.endDate;

      const contactFields = {};
      if (formData.contact.fullName)
        contactFields.fullName = formData.contact.fullName;
      if (formData.contact.jobTitle)
        contactFields.jobTitle = formData.contact.jobTitle;
      if (formData.contact.email) contactFields.email = formData.contact.email;
      if (formData.contact.phone) contactFields.phone = formData.contact.phone;
      if (Object.keys(contactFields).length > 0)
        updateData.contact = contactFields;

      const orgFields = {};
      if (formData.org.name) orgFields.name = formData.org.name;
      if (formData.org.address1) orgFields.address1 = formData.org.address1;
      if (formData.org.address2) orgFields.address2 = formData.org.address2;
      if (formData.org.city) orgFields.city = formData.org.city;
      if (formData.org.state) orgFields.state = formData.org.state;
      if (formData.org.region) orgFields.region = formData.org.region;
      if (formData.org.postcode) orgFields.postcode = formData.org.postcode;
      if (formData.org.country) orgFields.country = formData.org.country;
      if (formData.org.vatNumber) orgFields.vatNumber = formData.org.vatNumber;
      if (Object.keys(orgFields).length > 0) updateData.org = orgFields;

      await updateQuotation(id, updateData);
      toast.success("Quotation updated successfully");
      navigate(`/quotations/${id}`);
    } catch (error) {
      console.error("Failed to update quotation:", error);
      toast.error(error.message || "Failed to update quotation");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Please enter a note");
      return;
    }
    try {
      setAddingNote(true);
      const response = await addQuotationNote(id, newNote.trim());
      if (response?.success && response?.data?.note) {
        setNotes((prev) => [...prev, response.data.note]);
        setNewNote("");
        toast.success("Note added successfully");
      } else {
        throw new Error(response?.error || "Failed to add note");
      }
    } catch (error) {
      console.error("Failed to add note:", error);
      toast.error(error.message || "Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const handleAddSupportDocument = async (newDoc) => {
    if (!id) return;
    try {
      setUploadingDocument(true);
      let fileUrl = newDoc.fileUrl;
      if (newDoc.file && !fileUrl) {
        fileUrl = await uploadFileAndGetUrl(newDoc.file);
      }
      const feeValue =
        newDoc.fee === "£0.00" || !newDoc.fee
          ? 0
          : parseFloat(
              String(newDoc.fee).replace(/£/g, "").replace(/,/g, ""),
            ) || 0;
      await createQuotationDocument(id, {
        name: newDoc.name,
        detail: newDoc.detail || "",
        fee: feeValue,
        fileUrl,
        isLocked: newDoc.isLocked !== undefined ? newDoc.isLocked : true,
      });
      toast.success("Support document added");
      setIsAddDocumentModalOpen(false);
      await loadQuotation();
    } catch (err) {
      console.error("Failed to add support document:", err);
      toast.error(err.message || "Failed to add support document");
    } finally {
      setUploadingDocument(false);
    }
  };

  const formatNoteDate = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  const supportDocuments = quotationData?.supportDocuments || [];

  if (loading) {
    return (
      <div className="bg-[#fafafa] min-h-screen p-6">
        <div className="max-w-4xl mx-auto bg-white rounded-lg p-8">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#fafafa] min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/quotations")}
                className="px-4 py-2 h-[40px] border border-[#e7ebe9] bg-white text-[#485b54] rounded-[8px] text-[14px] font-medium hover:bg-[#F8F9FA] transition-colors flex items-center gap-2"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 12L6 8L10 4" />
                </svg>
                Back
              </button>
              <h1 className="text-2xl font-semibold text-[#222f2a]">
                Edit Quotation
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/quotations/${id}`)}
                className="px-6 py-2 h-[40px] border border-[#e7ebe9] bg-white text-[#485b54] rounded-[8px] text-[14px] font-medium hover:bg-[#F8F9FA] transition-colors"
              >
                View
              </button>
              <button
                onClick={() => navigate(`/quotations/${id}`)}
                className="px-4 py-2 text-sm text-[#73827c] hover:text-[#222f2a]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        <div className="flex gap-6">
          <div className="flex-1">
            <div className="bg-white rounded-lg shadow-sm border border-[#f2f2f2] p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Organisation Type
                  </label>
                  <select
                    name="organizationType"
                    value={formData.organizationType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-[#E5E7EB] rounded-lg appearance-none text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                  >
                    <option value="">Select organisation type</option>
                    {ORGANIZATION_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Intended Use
                  </label>
                  <textarea
                    name="intendedUse"
                    value={formData.intendedUse}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191] resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    eCOA
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="eCOA"
                        checked={formData.eCOA === true}
                        onChange={() =>
                          setFormData({ ...formData, eCOA: true })
                        }
                        className="w-4 h-4 text-[#40C191] border-[#E5E7EB] focus:ring-[#40C191]"
                      />
                      <span className="ml-2 text-[#485B54]">Yes</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="eCOA"
                        checked={formData.eCOA === false}
                        onChange={() =>
                          setFormData({ ...formData, eCOA: false })
                        }
                        className="w-4 h-4 text-[#40C191] border-[#E5E7EB] focus:ring-[#40C191]"
                      />
                      <span className="ml-2 text-[#485B54]">No</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Customer Action
                  </label>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      placeholder="Key"
                      value={formData.customerAction?.key ?? ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          customerAction: {
                            ...(prev.customerAction || {}),
                            key: e.target.value,
                          },
                        }))
                      }
                      className="flex-1 min-w-[120px] px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] placeholder:text-[#73827c] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                    />
                    <input
                      type="text"
                      placeholder="Value"
                      value={formData.customerAction?.value ?? ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          customerAction: {
                            ...(prev.customerAction || {}),
                            value: e.target.value,
                          },
                        }))
                      }
                      className="flex-1 min-w-[120px] px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] placeholder:text-[#73827c] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Translations
                  </label>
                  <div className="space-y-2">
                    <div className="px-4 py-3 border border-[#E5E7EB] rounded-lg bg-[#fafafa]">
                      <p className="text-sm text-[#485B54]">
                        {getSelectedTranslationsDisplay()}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleOpenTranslationModal}
                      className="px-4 py-2 text-sm font-medium text-white bg-[#40C191] hover:bg-[#35A47C] rounded-lg"
                    >
                      Select Translations
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Support documents
                  </label>
                  <div className="space-y-1 w-64 max-w-full">
                    {supportDocuments.length === 0 ? (
                      <p className="text-sm text-[#73827c] py-1">
                        No support documents yet.
                      </p>
                    ) : (
                      <ul className="border border-[#E5E7EB] rounded-md divide-y divide-[#E5E7EB] overflow-hidden">
                        {supportDocuments.map((doc) => (
                          <li
                            key={doc._id}
                            className="flex items-center justify-between gap-2 px-3 py-2 bg-white hover:bg-[#fafafa]"
                          >
                            <div className="min-w-0 flex-1">
                              <span className="text-sm font-medium text-[#222f2a] block truncate">
                                {doc.name || "Unnamed"}
                              </span>
                              {doc.detail && (
                                <p
                                  className="text-xs text-[#73827c] truncate"
                                  title={doc.detail}
                                >
                                  {doc.detail.length > 20
                                    ? doc.detail.slice(0, 20) + "…"
                                    : doc.detail}
                                </p>
                              )}
                              {doc.fee != null && (
                                <span className="text-xs text-[#73827c]">
                                  £{Number(doc.fee)}
                                </span>
                              )}
                            </div>
                            {doc.fileUrl && (
                              <a
                                href={doc.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-[#40C191] hover:underline shrink-0"
                              >
                                View
                              </a>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    <button
                      type="button"
                      onClick={() => setIsAddDocumentModalOpen(true)}
                      disabled={uploadingDocument}
                      className="px-4 py-2 text-sm font-medium text-white bg-[#40C191] hover:bg-[#35A47C] rounded-lg disabled:opacity-50"
                    >
                      {uploadingDocument
                        ? "Uploading…"
                        : "Upload support document"}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Number of Users
                  </label>
                  <input
                    type="number"
                    name="numUsers"
                    value={formData.numUsers}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Number of Uses
                  </label>
                  <input
                    type="number"
                    name="numUses"
                    value={formData.numUses}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    PQ / Trust Number
                  </label>
                  <input
                    type="text"
                    name="pqTrustNumber"
                    value={formData.pqTrustNumber}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#485b54] mb-2">
                    Territory (one per line)
                  </label>
                  <textarea
                    value={(formData.territory || []).join("\n")}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        territory: e.target.value
                          .split("\n")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      }))
                    }
                    rows={2}
                    className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191] resize-none"
                  />
                </div>

                <div className="border-t border-[#ebebeb] pt-6">
                  <h3 className="text-lg font-semibold text-[#222f2a] mb-4">
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#485b54] mb-2">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="contact.fullName"
                        value={formData.contact.fullName}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#485b54] mb-2">
                        Job Title
                      </label>
                      <input
                        type="text"
                        name="contact.jobTitle"
                        value={formData.contact.jobTitle}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#485b54] mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        name="contact.email"
                        value={formData.contact.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#485b54] mb-2">
                        Phone
                      </label>
                      <input
                        type="text"
                        name="contact.phone"
                        value={formData.contact.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t border-[#ebebeb] pt-6">
                  <h3 className="text-lg font-semibold text-[#222f2a] mb-4">
                    Organisation Details
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-[#485b54] mb-2">
                        Organisation Name
                      </label>
                      <input
                        type="text"
                        name="org.name"
                        value={formData.org.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#485b54] mb-2">
                        Address Line 1
                      </label>
                      <input
                        type="text"
                        name="org.address1"
                        value={formData.org.address1}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#485b54] mb-2">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        name="org.address2"
                        value={formData.org.address2}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#485b54] mb-2">
                          City
                        </label>
                        <input
                          type="text"
                          name="org.city"
                          value={formData.org.city}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#485b54] mb-2">
                          State
                        </label>
                        <input
                          type="text"
                          name="org.state"
                          value={formData.org.state}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-[#485b54] mb-2">
                          Region
                        </label>
                        <input
                          type="text"
                          name="org.region"
                          value={formData.org.region}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-[#485b54] mb-2">
                          Postcode
                        </label>
                        <input
                          type="text"
                          name="org.postcode"
                          value={formData.org.postcode}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#485b54] mb-2">
                        Country
                      </label>
                      <input
                        type="text"
                        name="org.country"
                        value={formData.org.country}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#485b54] mb-2">
                        VAT Number
                      </label>
                      <input
                        type="text"
                        name="org.vatNumber"
                        value={formData.org.vatNumber}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] focus:outline-none focus:ring-1 focus:ring-[#40C191]"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-4 pt-6 border-t border-[#ebebeb]">
                  <button
                    type="button"
                    onClick={() => navigate(`/quotations/${id}`)}
                    className="px-6 py-2 text-sm font-medium text-[#73827c] hover:text-[#222f2a]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-2 text-sm font-medium text-white bg-[#40C191] hover:bg-[#35A47C] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </form>
            </div>
          </div>

          <div className="w-80 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-sm border border-[#f2f2f2] p-6 sticky top-6">
              <h2 className="text-lg font-semibold text-[#222f2a] mb-4">
                Notes
              </h2>
              <div className="mb-6">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Add a note..."
                  rows={4}
                  className="w-full px-4 py-3 border border-[#E5E7EB] rounded-lg text-[#485B54] text-sm focus:outline-none focus:ring-1 focus:ring-[#40C191] resize-none mb-3"
                />
                <button
                  type="button"
                  onClick={handleAddNote}
                  disabled={addingNote || !newNote.trim()}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-[#40C191] hover:bg-[#35A47C] rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingNote ? "Adding..." : "Add Note"}
                </button>
              </div>
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {notes.length === 0 ? (
                  <p className="text-sm text-[#73827c] text-center py-4">
                    No notes yet
                  </p>
                ) : (
                  notes
                    .slice()
                    .reverse()
                    .map((n, index) => (
                      <div
                        key={index}
                        className="border border-[#E5E7EB] rounded-lg p-4 bg-[#fafafa]"
                      >
                        <p className="text-sm text-[#485B54] mb-2 whitespace-pre-wrap">
                          {n.note}
                        </p>
                        <div className="flex items-center justify-between text-xs text-[#73827c]">
                          <span>{n.addedBy?.fullName || "Unknown User"}</span>
                          <span>{formatNoteDate(n.addedAt)}</span>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        </div>

        {coa && (
          <LocalizationModal
            isOpen={isTranslationModalOpen}
            onClose={() => setIsTranslationModalOpen(false)}
            onSave={handleTranslationModalSave}
            translations={coaTranslations}
            coa={coa}
            wantOriginalTranslation={formData.wantOriginalTranslation}
            initialTerritory={formData.territory?.[0] || ""}
            initialSelectedLocales={formData.translationsRequested || []}
            initialCustomRequest={formData.customTranslationsText || ""}
          />
        )}

        <AddSupportDocumentModal
          isOpen={isAddDocumentModalOpen}
          onClose={() => setIsAddDocumentModalOpen(false)}
          onAdd={handleAddSupportDocument}
        />
      </div>
    </div>
  );
};

export default EditQuotation;
